import React, { useState, useEffect } from "react";
import axios from '../../../api/axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Avatar,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import useData from '../../../Hooks/useData';
import styles from '../forms/UserEditForm.module.css';

const AddUser = () => {
  const { auth } = useData();
  const token = auth?.token;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    FullName: '',
    Email: '',
    PreferredName: '',
    UserEnabled: true,
    BP_DOB: '',
    BP_TermsDate: '',
    Gender: '',
    RoleID: '',
    BP_BGCheckDate: '',
    BP_BGCheckText: '',
    BP_BadgeFlagline: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [roles, setRoles] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Image upload states
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');

  const genderOptions = [
    { value: '', label: 'Please select' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  // Fetch roles from API
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get('/api/basic/roles');
        const rolesList = [
          { value: '', label: 'Please select' },
          ...response.data.map(role => ({
            value: role.RoleID,
            label: role.RoleDisplayName
          }))
        ];
        setRoles(rolesList);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      }
    };
    fetchRoles();
  }, []);

  const handleFieldChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle image selection (store locally until user is created)
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    setProfileImage(file);
    setProfileImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfileImagePreview('');
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.FullName || formData.FullName.trim() === '') {
      setError('Full Name is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.Email || formData.Email.trim() === '') {
      setError('Email is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const requestConfig = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      };

      // Split full name into first/last
      const nameParts = formData.FullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Build user data for creation
      const userData = {
        UserName: formData.Email,
        Email: formData.Email,
        FirstName: firstName,
        LastName: lastName,
        FullName: formData.FullName,
        UserEnabled: formData.UserEnabled,
        BP_DOB: formData.BP_DOB || null,
        UserPassword: 'TempPassword123!', // Temporary password - user should reset on first login
        UserLastModified: new Date().toISOString()
      };

      // Create user
      const response = await axios.post('/api/users', JSON.stringify({ data: userData }), requestConfig);
      const createdUserId = response.data.UserID || response.data.id;

      // Upload profile image if provided
      if (profileImage && createdUserId) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('file', profileImage);

          await axios.post(`/api/users/${createdUserId}/profile-picture`, imageFormData, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            withCredentials: true
          });
        } catch (imageError) {
          console.error('Error uploading profile image:', imageError);
          // Don't fail the entire operation if image upload fails
        }
      }

      // Update role if provided
      if (formData.RoleID !== undefined && formData.RoleID !== null && formData.RoleID !== '' && createdUserId) {
        try {
          await axios.put(`/api/users/${createdUserId}/role`, { RoleID: Number(formData.RoleID) }, requestConfig);
        } catch (roleError) {
          console.error('Error setting role:', roleError);
        }
      }

      // Update user settings (gender + terms acceptance date)
      if (createdUserId) {
        try {
          const settingsUpdateData = {};

          if (formData.Gender !== undefined && formData.Gender !== null && formData.Gender !== '') {
            const genderMap = {
              'Male': 1,
              'Female': 0,
              'Other': 3,
              'Prefer not to say': 2
            };
            const genderValue = genderMap[formData.Gender];
            settingsUpdateData.UserGender = genderValue;
            settingsUpdateData.BP_P_Gender = String(genderValue);
          }

          if (formData.BP_TermsDate !== undefined && formData.BP_TermsDate && String(formData.BP_TermsDate).trim() !== '') {
            const d = new Date(formData.BP_TermsDate);
            settingsUpdateData.BP_TermsDate = !isNaN(d.getTime()) ? d.toISOString() : null;
          }

          // Background Check Date
          if (formData.BP_BGCheckDate && String(formData.BP_BGCheckDate).trim() !== '') {
            const d = new Date(formData.BP_BGCheckDate);
            settingsUpdateData.BP_BGCheckDate = !isNaN(d.getTime()) ? d.toISOString() : null;
          }

          // Background Check Notes
          if (formData.BP_BGCheckText && String(formData.BP_BGCheckText).trim() !== '') {
            settingsUpdateData.BP_BGCheckText = formData.BP_BGCheckText;
          }

          // Badge Flag Line
          if (formData.BP_BadgeFlagline && String(formData.BP_BadgeFlagline).trim() !== '') {
            settingsUpdateData.BP_BadgeFlagline = formData.BP_BadgeFlagline;
          }

          // Preferred Name (UserNickName)
          if (formData.PreferredName && String(formData.PreferredName).trim() !== '') {
            settingsUpdateData.UserNickName = formData.PreferredName;
          }

          if (Object.keys(settingsUpdateData).length > 0) {
            await axios.put(`/api/users/usersettings/${createdUserId}`, settingsUpdateData, requestConfig);
          }
        } catch (settingsError) {
          console.error('Error updating user settings:', settingsError);
        }
      }

      // Show success message
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Redirect to users list after a brief delay
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);

    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.message || error.response?.data?.error || error.message || "Failed to create user. Please try again.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.headerSection}>
        <Button
          type="button"
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          className={styles.backButton}
        >
          Back to Users
        </Button>
        <Typography variant="h4" className={styles.pageTitle}>
          Add New User
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" className={styles.errorAlert} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className={styles.successAlert}>
          User created successfully! Redirecting to users list...
        </Alert>
      )}

      <Paper className={styles.formPaper}>
        {/* Profile Picture Section */}
        <Box className={styles.profileSection}>
          <Typography variant="h6" className={styles.profileTitle}>
            PROFILE PICTURE
          </Typography>

          <Avatar
            src={profileImagePreview}
            className={styles.profileAvatar}
          />

          <Box className={styles.profileButtonsContainer}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              id="image-upload-input"
            />
            <label htmlFor="image-upload-input">
              <Button
                type="button"
                variant="contained"
                component="span"
                startIcon={<EditIcon />}
                className={styles.changeImageButton}
              >
                {profileImage ? 'CHANGE IMAGE' : 'ADD IMAGE'}
              </Button>
            </label>

            {profileImage && (
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={handleRemoveImage}
              >
                REMOVE IMAGE
              </Button>
            )}
          </Box>

          <Typography variant="caption" className={styles.imageCaption}>
            JPG, PNG, GIF (Max 5MB)
          </Typography>
        </Box>

        {/* User Information Section */}
        <Typography variant="h6" className={styles.formTitle}>
          USER INFORMATION
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Full Name *
              </Typography>
              <TextField
                fullWidth
                label="Full Name"
                name="FullName"
                value={formData.FullName}
                onChange={handleFieldChange}
                required
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Preferred Name
              </Typography>
              <TextField
                fullWidth
                label="Preferred Name"
                name="PreferredName"
                value={formData.PreferredName}
                onChange={handleFieldChange}
                helperText="Optional: Name to display on badge and in communications"
                className={styles.textField}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Email *
              </Typography>
              <TextField
                fullWidth
                label="Email"
                name="Email"
                type="email"
                value={formData.Email}
                onChange={handleFieldChange}
                required
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Date of Birth
              </Typography>
              <TextField
                fullWidth
                label="Date of Birth"
                name="BP_DOB"
                type="date"
                value={formData.BP_DOB}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Role
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Role</InputLabel>
                <Select
                  name="RoleID"
                  value={formData.RoleID || ''}
                  onChange={handleFieldChange}
                  label="Role"
                  displayEmpty
                  className={styles.selectField}
                >
                  {roles.map((role) => (
                    <MenuItem
                      key={role.value || 'empty'}
                      value={role.value}
                      className={role.value === '' && formData.RoleID ? styles.selectMenuItemHidden : styles.selectMenuItem}
                    >
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Gender
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Gender</InputLabel>
                <Select
                  name="Gender"
                  value={formData.Gender || ''}
                  onChange={handleFieldChange}
                  label="Gender"
                  displayEmpty
                  className={styles.selectField}
                >
                  {genderOptions.map((option) => (
                    <MenuItem
                      key={option.value || 'empty'}
                      value={option.value}
                      className={option.value === '' && formData.Gender ? styles.selectMenuItemHidden : styles.selectMenuItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Terms Acceptance Date
              </Typography>
              <TextField
                fullWidth
                label="Terms Acceptance Date"
                name="BP_TermsDate"
                type="datetime-local"
                value={formData.BP_TermsDate || ''}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.switchFieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Account Status
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.UserEnabled}
                    onChange={handleFieldChange}
                    name="UserEnabled"
                  />
                }
                label="User Enabled"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Background Check Date
              </Typography>
              <TextField
                fullWidth
                label="Background Check Date"
                name="BP_BGCheckDate"
                type="datetime-local"
                value={formData.BP_BGCheckDate || ''}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Background Check Notes
              </Typography>
              <TextField
                fullWidth
                label="Background Check Notes"
                name="BP_BGCheckText"
                value={formData.BP_BGCheckText || ''}
                onChange={handleFieldChange}
                multiline
                rows={3}
                helperText="Enter comments about background check"
                className={styles.textareaField}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Badge Flag Line
              </Typography>
              <TextField
                fullWidth
                label="Badge Flag Line"
                name="BP_BadgeFlagline"
                value={formData.BP_BadgeFlagline || ''}
                onChange={handleFieldChange}
                helperText="Optional text displayed on yellow flag (e.g., 'Tester', 'Staff', etc.)"
                className={styles.textField}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box className={styles.formActions}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create User'}
          </Button>
        </Box>
      </Paper>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          className={styles.snackbarAlert}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddUser;
